import { useRef, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Info } from 'lucide-react'
import {
  AddSelectedItem,
  ClearSetItem,
  MenuDivider,
  MenuFooter,
  ToggleItem,
  ViewSetItem,
} from './set-context-menu/set-context-menu.parts'
import { clampMenuPosition, useContextMenuClose } from './set-context-menu/use-context-menu-close'

const MENU_WIDTH_PX = 220
const MENU_HEIGHT_PX = 280

interface CollectionButtonContextMenuProps {
  x: number
  y: number
  isInCollectionSet: boolean
  hasSelection: boolean
  selectionCount: number
  collectionSetCount: number
  onClose: () => void
  onToggle: () => void
  onAddSelected: () => void
  onViewCollectionSet: () => void
  onClearCollectionSet: () => void
}

export function CollectionButtonContextMenu(props: CollectionButtonContextMenuProps): ReactElement {
  const {
    x,
    y,
    isInCollectionSet,
    hasSelection,
    selectionCount,
    collectionSetCount,
    onClose,
    onToggle,
    onAddSelected,
    onViewCollectionSet,
    onClearCollectionSet,
  } = props
  const { t } = useTranslation('cases')
  const menuRef = useRef<HTMLDivElement>(null)
  useContextMenuClose(menuRef, onClose)
  const position = clampMenuPosition({ x, y, widthPx: MENU_WIDTH_PX, heightPx: MENU_HEIGHT_PX })
  const wrap = (action: () => void) => () => {
    action()
    onClose()
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
      style={position}
    >
      <ToggleItem
        isInSet={isInCollectionSet}
        addLabel={t('infrastructure.addToCollectionSet')}
        removeLabel={t('infrastructure.removeFromCollectionSet')}
        onClick={wrap(onToggle)}
      />
      <AddSelectedItem
        hasSelection={hasSelection}
        selectionCount={selectionCount}
        label={t('infrastructure.addSelectedToSet')}
        enabledTitle={t('infrastructure.addSelectedToCollectionSetTitle', {
          count: selectionCount,
        })}
        disabledTitle={t('infrastructure.selectEndpointsFirst')}
        onClick={wrap(onAddSelected)}
      />
      <MenuDivider />
      <ViewSetItem
        label={t('infrastructure.viewCollectionSet')}
        count={collectionSetCount}
        onClick={wrap(onViewCollectionSet)}
      />
      <MenuDivider />
      <ClearSetItem
        label={t('infrastructure.clearCollectionSet')}
        count={collectionSetCount}
        onClick={wrap(onClearCollectionSet)}
      />
      <MenuFooter icon={Info} message={t('infrastructure.collectionSetSessionOnly')} />
    </div>
  )
}
